import React, { useEffect, useMemo, useState } from "react";
import {
  DialogContent,
  DialogContentText,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";

import Popup from "../../../../../components/common/model/Popup";
import SAVE_ICON from "../../../../../assets/check-mark.png";
import closeIcon from "../../../../../assets/closeModel.png";
import useStyles from "../../../../../components/common/model/styles";

import {
  INVOICE_OPTION_ROWS,
  initialInvoiceFormData,
  InvoiceGenerateFormData,
} from "./storageInvoiceFieldUtils";
import { useDispatch, useSelector } from "react-redux";
import { invoiceGeneratedAction, setInvoiceGeneratedResponse } from "../../../../../store/reducers/contractStorageSlice";
import Notification from "../../../../../components/common/notification/Notification";

dayjs.extend(advancedFormat);

const GREEN = "#1FA24A";

interface ManageInvoiceGenerateProps {
  open: boolean;
  onHide: () => void;
  title: string;
  lastInvoiceText?: string;
  // onCreate: (data: InvoiceGeneratePayload) => void;
  selectedRow: any[];
}

interface InvoiceCalculation {
  nextInvoiceDate: Dayjs | null;
  quantity: number;
}

interface InvoiceGeneratePayload {
  contractIds: string[];
  leadId: string;
  option: InvoiceGenerateFormData["option"];
  terminationDate: string;
}

const ManageInvoiceGenerate: React.FC<ManageInvoiceGenerateProps> = ({
  open,
  onHide,
  title,
  lastInvoiceText = "No invoice on file",
  // onCreate,
  selectedRow,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {isInvoiceGenerated} = useSelector((state: any) => state.contractStorage);
  const [formData, setFormData] = useState<InvoiceGenerateFormData>({
    ...initialInvoiceFormData,
  });

  const isMultipleSelected = selectedRow.length > 1;

  /**
   * Calculate invoice information for one contract.
   */
  const calculateInvoiceInfo = (contract: any): InvoiceCalculation => {
    const today = dayjs();

    const interval = contract?.invoiceInterval?.toLowerCase();

    const lastInvoiceDate = contract?.lastInvoiceDate
      ? dayjs(contract.lastInvoiceDate)
      : null;

      /**
   * DAILY
   */
  if (interval?.toLowerCase() === "daily") {
    if (lastInvoiceDate?.isValid()) {
      const daysPassed = today.startOf("day").diff(
        lastInvoiceDate.startOf("day"),
        "day",
      );

      return {
        nextInvoiceDate: lastInvoiceDate.add(1, "day"),
        quantity: Math.max(daysPassed, 1),
      };
    }

    return {
      nextInvoiceDate: today.add(1, "day"),
      quantity: 1,
    };
  }
    /**
     * WEEKLY
     */
    if (interval?.toLowerCase() === "weekly") {
      if (lastInvoiceDate?.isValid()) {
        const weeksPassed = today.diff(lastInvoiceDate, "week");

        return {
          nextInvoiceDate: lastInvoiceDate.add(1, "week"),
          quantity: Math.max(weeksPassed, 1),
        };
      }

      return {
        nextInvoiceDate: today.add(1, "week"),
        quantity: 1,
      };
    }

    /**
     * MONTHLY
     */
    if (interval?.toLowerCase() === "monthly") {
      if (lastInvoiceDate?.isValid()) {
        const monthsPassed = today
          .startOf("month")
          .diff(lastInvoiceDate.startOf("month"), "month");

        return {
          nextInvoiceDate: lastInvoiceDate.add(1, "month").startOf("month"),
          quantity: Math.max(monthsPassed, 1),
        };
      }

      return {
        nextInvoiceDate: today.add(1, "month").startOf("month"),
        quantity: 1,
      };
    }

    return {
      nextInvoiceDate: today,
      quantity: 1,
    };
  };

  /**
   * Calculate invoice information for all selected contracts.
   */
  const invoiceDetails = useMemo(() => {
    return selectedRow.map((contract: any) => {
      const calculation = calculateInvoiceInfo(contract);

      return {
        contract,
        ...calculation,
      };
    });
  }, [selectedRow]);

  /**
   * Format:
   *
   * 1 x Week 22nd Mar '25
   * 2 x Month 1st Oct '26
   */
  const formatInvoiceDescription = (
    contract: any,
    quantity: number,
    date: Dayjs | null,
  ) => {
    if (!date) return "";

    const interval =
      contract?.invoiceInterval?.charAt(0).toUpperCase() +
      contract?.invoiceInterval?.slice(1).toLowerCase();

    return `${quantity} x ${interval} ${date.format("Do MMM 'YY")}`;
  };

  /**
   * First selected contract.
   *
   * For single contract invoice this is the contract being invoiced.
   */
  const firstContract = selectedRow?.[0];

  const firstInvoiceDetails = invoiceDetails?.[0];

  /**
   * Reset / initialise form whenever modal opens.
   */
  useEffect(() => {
    if (!open) {
      setFormData({
        ...initialInvoiceFormData,
      });

      return;
    }

    /**
     * Multiple contracts:
     *
     * Only FINAL_INVOICE is allowed.
     * Current date is used by default.
     */
    if (selectedRow.length > 1) {
      setFormData((prev) => ({
        ...prev,
        option: "FINAL_INVOICE",
        terminationDate: dayjs().format("YYYY-MM-DD"),
      }));

      return;
    }

    /**
     * Single contract:
     *
     * Default option is the first option.
     * Use calculated next invoice date.
     */
    if (firstInvoiceDetails?.nextInvoiceDate) {
      setFormData((prev) => ({
        ...prev,
        terminationDate:
          firstInvoiceDetails.nextInvoiceDate?.format("YYYY-MM-DD") || "",
      }));
    }
  }, [open, selectedRow.length, firstInvoiceDetails?.nextInvoiceDate]);

  /**
   * Radio option change.
   */
  const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOption = e.target.value as InvoiceGenerateFormData["option"];

    /**
     * Multiple contracts can only generate FINAL invoice.
     */
    if (isMultipleSelected && selectedOption !== "FINAL_INVOICE") {
      return;
    }

    /**
     * FINAL INVOICE
     *
     * Default date = today
     */
    if (selectedOption === "FINAL_INVOICE") {
      setFormData((prev) => ({
        ...prev,
        option: selectedOption,
        terminationDate: dayjs().format("YYYY-MM-DD"),
      }));

      return;
    }

    /**
     * NEXT INVOICE
     *
     * Use calculated next invoice date.
     */
    setFormData((prev) => ({
      ...prev,
      option: selectedOption,
      terminationDate:
        firstInvoiceDetails?.nextInvoiceDate?.format("YYYY-MM-DD") || "",
    }));
  };

  /**
   * Final invoice date change.
   */
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      option: "FINAL_INVOICE",
      terminationDate: e.target.value,
    }));
  };

  /**
   * Create invoice payload.
   */
  const handleCreate = () => {
    const payload: InvoiceGeneratePayload = {
      /**
       * Multiple contract IDs
       */
      contractIds: selectedRow.map((contract) => contract?.id),

      /**
       * Current API expects one leadId.
       *
       * If multiple selected contracts belong to the same lead,
       * first leadId is enough.
       */
      leadId: firstContract?.storageLeadId || firstContract?.leadId || "",

      /**
       * Currently selected radio option.
       */
      option: formData.option,

      /**
       * Selected/calculated invoice date.
       */
      terminationDate: formData.terminationDate,
    };

    console.log("Invoice payload:", payload);
    dispatch(invoiceGeneratedAction(payload));
    // onCreate(payload);
  };

  useEffect(()=>{
    if(isInvoiceGenerated){
      dispatch(setInvoiceGeneratedResponse(false));
      onHide();
    }
  },[isInvoiceGenerated])

  return (
    <Popup
      open={open}
      title={title}
      onHide={onHide}
      fullWidth={true}
      maxWidth={"sm"}
      aria-describedby="manage-invoice-generate-dialog-description"
      popupBg="#FFFFFF"
      header={
        <>
          <img
            onClick={handleCreate}
            src={SAVE_ICON}
            className={classes.deleteicons}
            alt="save"
            style={{ cursor: "pointer" }}
          />

          <img
            onClick={onHide}
            src={closeIcon}
            className={classes.closeicons}
            style={{ cursor: "pointer" }}
            alt="close"
          />
        </>
      }
    >
      <DialogContent
        sx={{
          padding: "10px 24px 24px",
        }}
      >
        <DialogContentText
          component="div"
          id="manage-invoice-generate-dialog-description"
        >
          <Notification/>
          {/* ========================================= */}
          {/* SELECTED CONTRACTS */}
          {/* ========================================= */}

          {invoiceDetails.length > 0 && (
            <Box sx={{ mb: 1,px: "12px",
                py: "0px", }}>
              {invoiceDetails.map(({ contract }) => (
                <Box
                  key={contract?.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: "6px",
                  }}
                >
                  <Box
                    sx={{
                      width: 140,
                      flexShrink: 0,
                      fontSize: "15px",
                      color: "#8a8a8a",
                    }}
                  >
                    Last Invoice
                  </Box>

                  <Box
                    sx={{
                      fontSize: "15px",
                      color: "#8a8a8a",
                    }}
                  >
                    {contract?.lastInvoiceDate
                      ? dayjs(contract.lastInvoiceDate).format("DD/MM/YYYY")
                      : lastInvoiceText}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* ========================================= */}
          {/* MULTIPLE CONTRACT MESSAGE */}
          {/* ========================================= */}

          {isMultipleSelected && (
            <Box
              sx={{
                mb: 1,
                px: "12px",
                py: "8px",
                fontSize: "13px",
                color: "#777",
              }}
            >
              Multiple contracts selected. Only Final Invoice can be generated.
            </Box>
          )}

          {/* ========================================= */}
          {/* RADIO GROUP */}
          {/* ========================================= */}

          <RadioGroup value={formData.option} onChange={handleOptionChange}>
            {INVOICE_OPTION_ROWS.map((row) => {
              const selected = formData.option === row.value;

              const isFinalOption = row.value === "FINAL_INVOICE";

              /**
               * When multiple contracts are selected:
               *
               * FINAL_INVOICE -> enabled
               * Other options  -> disabled
               */
              const disabled = isMultipleSelected && !isFinalOption;

              /**
               * If you want FINAL_INVOICE to be visible only
               * for multiple contracts, change this condition.
               *
               * Currently:
               * - single -> all options visible
               * - multiple -> only FINAL visible
               */
              if (isMultipleSelected && !isFinalOption) {
                return null;
              }

              return (
                <Box
                  key={row.value}
                  onClick={() => {
                    if (disabled) return;

                    if (row.type === "text") {
                      handleOptionChange({
                        target: {
                          value: row.value,
                        },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: "10px",
                    px: "12px",
                    borderRadius: "6px",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    backgroundColor: selected ? "#E9F7EE" : "transparent",
                    transition: "background-color 0.15s ease",
                  }}
                >
                  {/* Label */}
                  <Box
                    sx={{
                      width: 140,
                      flexShrink: 0,
                      fontSize: "15px",
                      fontWeight: selected ? 700 : 600,
                      color: "#1a1a1a",
                    }}
                  >
                    {row.label}
                  </Box>

                  {/* Description / Date */}
                  <Box
                    sx={{
                      flex: 1,
                    }}
                  >
                    {row.type === "text" ? (
                      <Box
                        sx={{
                          fontSize: "15px",
                          fontWeight: selected ? 700 : 400,
                          color: "#1a1a1a",
                        }}
                      >
                        {row.description}

                        {/* ================================= */}
                        {/* NEXT INVOICE DESCRIPTION */}
                        {/* ================================= */}

                        {firstInvoiceDetails?.nextInvoiceDate && (
                          <Box
                            sx={{
                              fontSize: "13px",
                              color: "#777",
                              mt: "3px",
                            }}
                          >
                            {formatInvoiceDescription(
                              firstContract,
                              firstInvoiceDetails.quantity,
                              firstInvoiceDetails.nextInvoiceDate,
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <TextField
                        type="date"
                        size="small"
                        value={formData.terminationDate}
                        onChange={handleDateChange}
                        onClick={(e) => e.stopPropagation()}
                        disabled={disabled}
                        sx={{
                          width: "220px",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "6px",
                          },
                        }}
                      />
                    )}
                  </Box>

                  {/* Radio */}
                  <FormControlLabel
                    value={row.value}
                    disabled={disabled}
                    control={
                      <Radio
                        sx={{
                          color: GREEN,
                          "&.Mui-checked": {
                            color: GREEN,
                          },
                        }}
                      />
                    }
                    label=""
                    sx={{
                      m: 0,
                    }}
                  />
                </Box>
              );
            })}
          </RadioGroup>
        </DialogContentText>
      </DialogContent>
    </Popup>
  );
};

export default ManageInvoiceGenerate;
